import { type ReactElement } from 'react'

import {
  IconButton,
  IconMessage,
  WithBadge,
  WithPopover,
  type WithPopoverChildrenProps,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { ChatMenu, type IChatMenuProps } from './ChatMenu'
import messages from './messages'

export interface IWithChatMenuProps extends IChatMenuProps {
  /** Badge content; the caller owns the thread these belong to. */
  commentsCount: number
  renderTrigger?: (commentsCount: number) => ReactElement
}

/**
 * WithChatMenu presents a ChatMenu in a popover, anchored to a badged chat
 * button unless the caller renders its own trigger.
 */
export const WithChatMenu = ({
  commentsCount,
  colleaguesThread,
  externalThread,
  renderTrigger,
}: IWithChatMenuProps) => {
  const intl = useProcurifyIntl()

  const renderMenu = ({ dismiss }: WithPopoverChildrenProps) => (
    <ChatMenu
      colleaguesThread={colleaguesThread}
      externalThread={{
        ...externalThread,
        onOpen: () => {
          dismiss()
          externalThread.onOpen()
        },
      }}
    />
  )

  return (
    <WithPopover trigger='click' placement='bottom' content={renderMenu}>
      <div>
        {renderTrigger ? (
          renderTrigger(commentsCount)
        ) : (
          <WithBadge
            id='comment-count'
            ariaLabel={intl.formatMessage(messages.commentsCountAriaLabel, {
              count: commentsCount,
            })}
            content={commentsCount}
          >
            <IconButton
              aria-label={intl.formatMessage(messages.chatButtonAriaLabel)}
              iconComponent={<IconMessage />}
            />
          </WithBadge>
        )}
      </div>
    </WithPopover>
  )
}
